// Shared markdown viewer/editor used by the document detail's right-rail
// PreviewPane and the full ContentTab. Two responsibilities:
//
//   1. Render markdown with consistent styling (the .markdown-body class
//      stylesheet lives in globals.css; this component renders into it).
//   2. Toggle between rendered and source-edit mode via a small pencil
//      button in the top-right of the chrome. When editing, the textarea
//      is monospaced + autosized, with Save / Cancel buttons that wire
//      through PATCH /documents/:id { content }.
//
// We deliberately keep the edit affordance lightweight — this is a
// straight textarea, not a CodeMirror/Monaco embed. Rationale: most
// markdown documents in this product are notes/specs <100 KB; a textarea
// handles that comfortably and avoids pulling in another editor bundle.

import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeHighlight from 'rehype-highlight';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import 'highlight.js/styles/github.css';
import { splitFrontmatter } from '../lib/frontmatter.js';

// rehype-sanitize's default schema strips className from <code>/<span>/<pre>,
// which would kill rehype-highlight's `hljs hljs-keyword` etc. tokens. Extend
// the schema to allow class attributes on code-block elements only. Stays
// XSS-safe because we whitelist only those specific elements/attrs.
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code ?? []), 'className'],
    span: [...(defaultSchema.attributes?.span ?? []), 'className'],
    pre: [...(defaultSchema.attributes?.pre ?? []), 'className'],
  },
};

// rehype-highlight must run BEFORE rehype-sanitize so the syntax-highlight
// span tree exists when the sanitizer inspects it. The sanitizer then
// preserves the hljs classes per the schema above.
//
// detect: true   — auto-detect language when no fence info is provided.
// ignoreMissing  — silently skip unknown languages (e.g. ```cowsay) instead
//                   of throwing.
const rehypePlugins = [
  [rehypeHighlight, { detect: true, ignoreMissing: true }],
  [rehypeSanitize, sanitizeSchema],
  // CTSK-301 — GitHub-style heading ids (github-slugger under the hood:
  // "3. Listing + searching documents" → id="3-listing--searching-documents")
  // so /help/*#anchor deep links + in-doc anchor jumps have a target.
  //
  // Ordering matters: rehype-slug must run AFTER rehype-sanitize. The
  // sanitizer's defaultSchema "clobbers" id attributes with a
  // `user-content-` prefix (DOM-clobbering guard for untrusted markdown),
  // which would silently break document.getElementById(hash) lookups.
  // Running slug post-sanitize keeps the generated h1–h6 ids pristine;
  // it adds nothing else, so the sanitize guarantees still hold for the
  // authored content itself.
  rehypeSlug,
  // Free add via the plugin chain (ticket marks this ship-if-free): a
  // small `#` link appended inside each heading, hover-revealed via
  // .markdown-body .heading-anchor in globals.css. Runs after sanitize
  // too, but only injects our own anchor elements pointing at the ids
  // rehype-slug just generated.
  [rehypeAutolinkHeadings, {
    behavior: 'append',
    properties: { className: ['heading-anchor'], ariaHidden: true, tabIndex: -1 },
    content: { type: 'text', value: '#' },
  }],
] as Parameters<typeof ReactMarkdown>[0]['rehypePlugins'];

export interface MarkdownViewProps {
  /** The full document content (may include a leading frontmatter fence). */
  content: string;
  /** Show a pencil affordance + wire edit mode. When omitted, the view is
   *  read-only. */
  editable?: boolean;
  /** Called with the new content on Save. Should return a promise so the
   *  view can show a saving spinner + error toast. */
  onSave?: (next: string) => Promise<void>;
  /** Hides the rendered frontmatter banner — the side panel may already
   *  show it more thoroughly. */
  hideFrontmatterBanner?: boolean;
  /** Constrain text width for readability. Defaults to true; pass false
   *  for the right-rail preview where the column is already narrow. */
  constrainWidth?: boolean;
}

export function MarkdownView({
  content,
  editable = false,
  onSave,
  hideFrontmatterBanner = false,
  constrainWidth = true,
}: MarkdownViewProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Soft-wrap toggle for the editor. Off = code-editor feel
  // (gutter numbers align 1:1 with logical lines, horizontal scroll
  // for long lines). On = prose-editor feel (everything fits in the
  // pane; gutter numbers will visually drift on wrapped lines).
  // Persisted so the user's choice survives a reload.
  const WRAP_PREF_KEY = 'ctsk.markdownEditor.wrap';
  const [wrap, setWrap] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(WRAP_PREF_KEY) === 'on';
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // When the upstream content changes (refetch after a save), reset the
  // draft so the next edit starts from the canonical version.
  useEffect(() => { setDraft(content); }, [content]);

  // Autosize the textarea to its content (with a sensible cap).
  // Re-runs on `wrap` too: wrapping changes scrollHeight (soft-wrap
  // makes the textarea taller for the same source).
  useEffect(() => {
    if (mode !== 'edit' || !textareaRef.current) return;
    const ta = textareaRef.current;
    ta.style.height = 'auto';
    const cap = Math.min(ta.scrollHeight + 4, window.innerHeight * 0.7);
    ta.style.height = `${Math.max(cap, 240)}px`;
  }, [mode, draft, wrap]);

  function toggleWrap() {
    setWrap((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(WRAP_PREF_KEY, next ? 'on' : 'off');
      } catch {
        /* private mode etc. — preference just won't persist */
      }
      return next;
    });
  }

  // Split frontmatter for the rendered body — same trick the inline
  // PreviewPane was doing, now centralized here.
  const { rawFrontmatter, frontmatter, body } = useMemo(
    () => splitFrontmatter(content),
    [content],
  );
  const fmKeyCount = Object.keys(frontmatter).length;

  async function handleSave() {
    if (!onSave) return;
    if (draft === content) { setMode('view'); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setMode('view');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setDraft(content);
    setError(null);
    setMode('view');
  }

  const chromeStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  };

  return (
    <div style={{ width: '100%' }}>
      {editable && (
        <div style={chromeStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: 'var(--text-muted)' }}>
            <span>{mode === 'view' ? 'Rendered' : 'Editing source'}</span>
            {mode === 'edit' && (
              <button
                type="button"
                onClick={toggleWrap}
                title={
                  wrap
                    ? 'Soft-wrap is ON — long lines wrap to fit; gutter numbers can visually drift on wrapped lines. Click to turn off for a code-editor feel.'
                    : 'Soft-wrap is OFF — gutter numbers stay aligned 1:1 with source lines; long lines scroll horizontally. Click to turn on for a prose-editor feel.'
                }
                aria-pressed={wrap}
                aria-label={`Soft-wrap is ${wrap ? 'on' : 'off'}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '2px 8px',
                  height: 22,
                  borderRadius: 999,
                  border: `1px solid ${wrap ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                  background: wrap
                    ? 'color-mix(in srgb, var(--accent-primary) 12%, transparent)'
                    : 'var(--surface-base)',
                  color: wrap ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                <span aria-hidden>↵</span>
                <span>Wrap: {wrap ? 'on' : 'off'}</span>
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {mode === 'view' ? (
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setMode('edit')}
                title="Edit source"
                aria-label="Edit document content"
                style={{ padding: '2px 8px', fontSize: 13 }}
              >
                ✎ Edit
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={handleCancel}
                  disabled={saving}
                  style={{ padding: '2px 8px', fontSize: 13 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleSave}
                  disabled={saving || draft === content}
                  style={{ padding: '2px 10px', fontSize: 13 }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {mode === 'edit' ? (
        <>
          <LineNumberedTextarea
            textareaRef={textareaRef}
            value={draft}
            onChange={(v) => setDraft(v)}
            wrap={wrap}
          />
          {error && (
            <div style={{ color: 'var(--danger-primary)', fontSize: 12, marginTop: 6 }}>
              {error}
            </div>
          )}
        </>
      ) : (
        <>
          {rawFrontmatter !== null && !hideFrontmatterBanner && (
            <div
              style={{
                background: 'var(--surface-canvas)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 4,
                padding: '6px 10px',
                marginBottom: 12,
                fontSize: 11,
                color: 'var(--text-muted)',
              }}
            >
              <strong style={{ color: 'var(--text-primary)' }}>frontmatter</strong>
              {' · '}{fmKeyCount} field{fmKeyCount === 1 ? '' : 's'}
              {' · '}see panel on the right
            </div>
          )}
          <article
            className="markdown-body"
            style={constrainWidth ? { maxWidth: '74ch', marginInline: 'auto' } : undefined}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={rehypePlugins}>
              {body}
            </ReactMarkdown>
          </article>
        </>
      )}
    </div>
  );
}

// ─── LineNumberedTextarea ───────────────────────────────────────
//
// Lightweight gutter+textarea pair. The gutter renders 1..N in a
// monospace <pre>; scroll is synced from the textarea via onScroll
// so vertical scroll stays aligned when the doc is taller than the
// editor cap (70vh). Soft-wrap is OFF so logical lines align with
// gutter rows — horizontal scroll handles long lines (same as
// every code editor). Still a single textarea under the hood, so
// undo/redo/select/copy continue to behave like a native control.

function LineNumberedTextarea({
  textareaRef,
  value,
  onChange,
  wrap,
}: {
  textareaRef: RefObject<HTMLTextAreaElement>;
  value: string;
  onChange: (next: string) => void;
  /** When true, long lines visually wrap (gutter numbers may drift on
   *  wrapped rows). When false, source lines stay 1:1 with gutter
   *  numbers and overflow scrolls horizontally. */
  wrap: boolean;
}) {
  const gutterRef = useRef<HTMLPreElement>(null);
  const lineCount = useMemo(() => {
    // Always show at least one line, even on empty content, so the
    // gutter never reads "0" or looks broken.
    const n = (value.match(/\n/g)?.length ?? 0) + 1;
    return n;
  }, [value]);
  const gutterText = useMemo(
    () => Array.from({ length: lineCount }, (_, i) => String(i + 1)).join('\n'),
    [lineCount],
  );
  // Reserve gutter width based on digit count + small padding so the
  // textarea doesn't reflow when crossing 9→10→100.
  const gutterChars = String(lineCount).length;

  const handleScroll = () => {
    const ta = textareaRef.current;
    const g = gutterRef.current;
    if (ta && g) g.scrollTop = ta.scrollTop;
  };

  const sharedTextStyle: React.CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontSize: 13,
    lineHeight: 1.55,
    margin: 0,
  };

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        border: '1px solid var(--border-strong)',
        borderRadius: 6,
        background: 'var(--surface-canvas)',
        overflow: 'hidden',
      }}
    >
      <pre
        ref={gutterRef}
        aria-hidden="true"
        style={{
          ...sharedTextStyle,
          flex: '0 0 auto',
          // ch unit + 20px of padding (8px right, 12px left) keeps
          // the number column tight against the editor.
          width: `calc(${gutterChars}ch + 20px)`,
          padding: '12px 8px 12px 12px',
          textAlign: 'right',
          color: 'var(--text-muted)',
          background: 'var(--surface-sunken)',
          borderRight: '1px solid var(--border-subtle)',
          overflow: 'hidden',
          userSelect: 'none',
          whiteSpace: 'pre',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {gutterText}
      </pre>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={handleScroll}
        // wrap=off  → logical lines stay aligned with gutter numbers
        //             (horizontal scroll for long lines).
        // wrap=soft → long lines wrap to fit the pane (gutter numbers
        //             may visually drift on wrapped rows).
        wrap={wrap ? 'soft' : 'off'}
        spellCheck={false}
        style={{
          ...sharedTextStyle,
          flex: 1,
          minHeight: 240,
          padding: '12px',
          border: 'none',
          outline: 'none',
          resize: 'vertical',
          background: 'transparent',
          color: 'var(--text-primary)',
          // pre vs pre-wrap controls whether the textarea wraps long
          // lines. Stay scrollable in both modes: with wrap off, that
          // means horizontal scroll; with wrap on, vertical only.
          whiteSpace: wrap ? 'pre-wrap' : 'pre',
          wordBreak: wrap ? 'break-word' : 'normal',
          overflow: 'auto',
        }}
      />
    </div>
  );
}
